"use client";

import { FormEvent, useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";
import { registerPartner } from "@/utils/partner";

type ApplyFormState = {
  email: string;
  password: string;
  companyName: string;
  website: string;
};

const initialState: ApplyFormState = {
  email: "",
  password: "",
  companyName: "",
  website: "",
};

export default function ApplyForm() {
  const router = useRouter();
  const [form, setForm] = useState<ApplyFormState>(initialState);
  const [loading, setLoading] = useState(false);

  const handleChange =
    (field: keyof ApplyFormState) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload: any = {
        email: form.email.trim(),
        companyName: form.companyName.trim(),
        website: form.website.trim(),
      };

      // only send password if provided — existing users don't need it
      if (form.password) {
        payload.password = form.password;
      }

      const res = await registerPartner(payload);

      const msg = res.existingUser
        ? "✅ Your existing account is linked. Pending admin approval."
        : "✅ Application received. You can now sign in to check your status.";

      toast.success(msg);
      router.push("/partner/login");

    } catch (err: any) {
      const serverError = err?.response?.data?.error;
      const status = err?.response?.data?.status;

      // already has a partner account → send to dashboard
      if (status === "active" || status === "pending") {
        toast.info("You already have a partner application. Redirecting to dashboard…");
        router.push("/partner/dashboard");
        return;
      }

      toast.error(
        serverError ||
        err?.message ||
        "Could not submit partner application."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mx-auto max-w-xl space-y-5 rounded-2xl border border-border bg-background p-6"
    >
      <div>
        <label className="mb-1 block text-sm font-medium">Company name</label>
        <input
          type="text"
          value={form.companyName}
          onChange={handleChange("companyName")}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
          placeholder="Acme Digital"
          required
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium">Website</label>
        <input
          type="url"
          value={form.website}
          onChange={handleChange("website")}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
          placeholder="https://acme.com"
          required
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium">Email</label>
        <input
          type="email"
          value={form.email}
          onChange={handleChange("email")}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
          placeholder="you@company.com"
          required
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium">
          Password
          <span className="ml-1 text-xs text-muted-foreground font-normal">
            (leave blank if you already have a ReloadPi account)
          </span>
        </label>
        <input
          type="password"
          value={form.password}
          onChange={handleChange("password")}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
          placeholder="Create a password or leave blank"
          minLength={form.password ? 8 : undefined}
        />
      </div>

      <div className="rounded-xl border border-border/60 bg-muted/30 p-4 text-sm space-y-1">
        <p className="font-medium">What happens next?</p>
        <p className="text-muted-foreground">
          Your application will be reviewed by our team. Once approved you will
          receive an email with your API key and referral code.
        </p>
        <p className="text-muted-foreground">
          Already have a ReloadPi account? Use that email — no new account needed.
        </p>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-md bg-primary px-4 py-2 text-white transition hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? "Submitting…" : "Apply as Partner"}
      </button>
    </form>
  );
}